export function isMissingValue(value) {
  return value == null || `${value}`.trim() === "";
}

export function humanizeLabel(value) {
  return (value ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/Nps/g, "NPS ")
    .replace(/Pct/g, " %")
    .replace(/FTE/g, " FTE")
    .replace(/YTD/g, " YTD")
    .replace(/LY/g, " LY")
    .replace(/WINZ/g, "WINZ")
    .trim();
}

export function getColourTone(colour) {
  if ((colour ?? "").trim().startsWith("#")) {
    return "custom";
  }

  const normalizedColour = (colour ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (["red", "error", "danger", "critical"].includes(normalizedColour)) {
    return "error";
  }

  if (["amber", "orange", "warning", "yellow"].includes(normalizedColour)) {
    return "warning";
  }

  if (["green", "success", "healthy"].includes(normalizedColour)) {
    return "success";
  }

  if (["blue", "info"].includes(normalizedColour)) {
    return "info";
  }

  return "neutral";
}

export function hexToRgba(hex, alpha = 1) {
  const normalizedHex = (hex ?? "").replace("#", "").trim();

  if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return null;
  }

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16);
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function getDecimalPlacesFromFormatting(formatting) {
  const format = formatting ?? "";

  const d3Match = format.match(/\.(\d+)[%f]/);
  if (d3Match) {
    return Number.parseInt(d3Match[1], 10);
  }

  const decimalSection = format.match(/\.([0#]+)/);
  return decimalSection?.[1]?.length ?? null;
}

export function formatValue(value, item) {
  if (isMissingValue(value)) {
    return "Needs input";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  const formatting = item.formatting ?? "";
  const decimalPlaces = getDecimalPlacesFromFormatting(formatting);

  if (formatting.includes("%")) {
    return new Intl.NumberFormat("en-NZ", {
      style: "percent",
      minimumFractionDigits: decimalPlaces ?? 0,
      maximumFractionDigits: decimalPlaces ?? 1,
    }).format(numericValue);
  }

  if (formatting.includes("$") || formatting.includes("\\$")) {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
      minimumFractionDigits: decimalPlaces ?? 2,
      maximumFractionDigits: decimalPlaces ?? 2,
    }).format(numericValue);
  }

  if (item.type === "int") {
    return new Intl.NumberFormat("en-NZ", {
      maximumFractionDigits: 0,
    }).format(numericValue);
  }

  if (item.type === "decimal") {
    return new Intl.NumberFormat("en-NZ", {
      minimumFractionDigits: decimalPlaces ?? 0,
      maximumFractionDigits: decimalPlaces ?? Math.min(4, `${value}`.split(".")[1]?.length ?? 0),
    }).format(numericValue);
  }

  return value;
}

const BAND_NAME_TO_STATUS = {
  fail: "failed",
  warning: "warning",
  passed: "passed",
};

export function getBandStatus(item) {
  const bands = item.bands;
  if (!bands || bands.length === 0) {
    return null;
  }

  if (isMissingValue(item.value)) {
    return null;
  }

  const numericValue = Number(item.value);
  if (Number.isNaN(numericValue)) {
    return null;
  }

  for (const band of [...bands].reverse()) {
    if (numericValue >= band.from && numericValue <= band.to) {
      const key = (band.name ?? "").trim().toLowerCase();
      return BAND_NAME_TO_STATUS[key] ?? null;
    }
  }

  return null;
}

export function getBandPresentation(item) {
  const status = getBandStatus(item);
  if (!status) {
    return undefined;
  }

  const colorMap = {
    passed: "var(--secondary-color)",
    warning: "var(--warning-color)",
    failed: "var(--error-color)",
  };

  return { color: colorMap[status] };
}

export function getBudgetIndicator(item) {
  if (item.budgetedValue == null) return null;

  const budgeted = Number(item.budgetedValue);
  if (Number.isNaN(budgeted)) return null;

  if (isMissingValue(item.value)) return null;
  const actual = Number(item.value);
  if (Number.isNaN(actual)) return null;

  const bands = item.bands;
  let higherIsBetter = true;

  if (bands?.length >= 2) {
    const passedBand = bands.find((b) => (b.name ?? "").trim().toLowerCase() === "passed");
    const failBand = bands.find((b) => (b.name ?? "").trim().toLowerCase() === "fail");

    if (passedBand && failBand) {
      higherIsBetter = passedBand.to >= failBand.to;
    }
  }

  if (actual === budgeted) {
    return { status: "on-track", direction: "equal" };
  }

  if (higherIsBetter) {
    return actual > budgeted
      ? { status: "on-track", direction: "above" }
      : { status: "off-track", direction: "below" };
  }

  return actual < budgeted
    ? { status: "on-track", direction: "below" }
    : { status: "off-track", direction: "above" };
}

export function getValuePresentation(item) {
  const bandStyle = getBandPresentation(item);
  if (bandStyle) {
    return bandStyle;
  }

  if (!item.colour) {
    return undefined;
  }

  if (item.colour.startsWith("#")) {
    return {
      borderColor: hexToRgba(item.colour, 0.45) ?? item.colour,
      backgroundColor: hexToRgba(item.colour, 0.12) ?? "transparent",
      color: item.colour,
    };
  }

  return undefined;
}

export function decimalToDisplayPercent(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return String(parseFloat((num * 100).toFixed(10)));
}

export function displayPercentToDecimal(value) {
  if (value == null || value === "" || value === "-") return value ?? "";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return String(parseFloat((num / 100).toFixed(10)));
}

export function getFormatSuffix(formatting) {
  const format = formatting ?? "";
  if (format.includes("%")) return "%";
  return null;
}

export function getInputMode(type) {
  if (type === "int") {
    return "numeric";
  }

  if (type === "decimal") {
    return "decimal";
  }

  return "text";
}

export function getPlaceholder(item) {
  if (item.type === "int") {
    return "Enter a whole number";
  }

  if (item.type === "decimal") {
    return "Enter a decimal value";
  }

  return "Enter a value";
}

export function isValidInputForFormat(value, formatting) {
  if (value === "" || value === "-") {
    return true;
  }

  const format = formatting ?? "";
  const decimalPlaces = getDecimalPlacesFromFormatting(format);
  const isNumericFormat = format.includes("%") || format.includes("f") || format.includes("$");

  if (isNumericFormat) {
    if (decimalPlaces === 0) {
      return /^-?\d*$/.test(value);
    }

    if (decimalPlaces != null) {
      return new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}$`).test(value);
    }

    return /^-?\d*\.?\d*$/.test(value);
  }

  return true;
}

export function createEmptyGoal(kpiDataId, groupName, fieldName) {
  return {
    id: 0,
    kpiDataId,
    previousMonthSuccess: "",
    kpiFocusFieldName: fieldName,
    kpiFocusGroupName: groupName,
    kpiFocusCommentary: "",
    impactRisk: "",
    target: "",
    specificAction: "",
  };
}

export function mergeGoal(goals, nextGoal) {
  const currentGoals = Array.isArray(goals) ? goals : [];
  const matchIndex = currentGoals.findIndex((goal) => (
    goal.id === nextGoal.id
    || (
      goal.kpiFocusFieldName === nextGoal.kpiFocusFieldName
      && goal.kpiFocusGroupName === nextGoal.kpiFocusGroupName
    )
  ));

  if (matchIndex === -1) {
    return [...currentGoals, nextGoal];
  }

  return currentGoals.map((goal, index) => (index === matchIndex ? nextGoal : goal));
}

export function mergeGoalReflection(goals, kpiGoalId, reflection) {
  return (Array.isArray(goals) ? goals : []).map((goal) => (
    goal.id === kpiGoalId
      ? { ...goal, reflection }
      : goal
  ));
}

export function normalizeDraft(scorecard, goals = scorecard?.goals) {
  if (!scorecard) {
    return null;
  }

  // Sort groups alphabetically so the order is deterministic regardless of the
  // order the API returns them in. Without this the categories can shuffle on a
  // hard refresh.
  const groups = Array.isArray(scorecard.groups)
    ? [...scorecard.groups].sort((a, b) =>
        (a.groupName ?? "").localeCompare(b.groupName ?? "", undefined, { sensitivity: "base" }))
    : scorecard.groups;

  return {
    ...scorecard,
    groups,
    goals: Array.isArray(goals) ? goals : [],
  };
}

export function serializeDraftForComparison(scorecard) {
  if (!scorecard) {
    return JSON.stringify(null);
  }
  const { feedbacks, ...rest } = scorecard;
  return JSON.stringify({ ...rest, goals: Array.isArray(rest.goals) ? rest.goals : [] });
}

export function findScorecardItem(scorecard, groupName, itemName) {
  return scorecard?.groups
    ?.find((group) => group.groupName === groupName)
    ?.items?.find((item) => (item.displayName ?? item.name) === itemName) ?? null;
}

export function formatScorecardPeriod(month, year) {
  if (!month || !year) {
    return "Unknown period";
  }

  const date = new Date(year, month - 1, 1);

  if (Number.isNaN(date.getTime())) {
    return "Unknown period";
  }

  return new Intl.DateTimeFormat("en-NZ", {
    month: "long",
    year: "numeric",
  }).format(date);
}

// The subordinates API can return either a flat list ("isGrouped": false) or
// staff grouped under area managers ("isGrouped": true). Flatten both shapes
// into a single array, tagging grouped entries with their area manager so the
// UI can decide whether to display groups.
export function normalizeSubordinates(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  if (response.isGrouped) {
    return (Array.isArray(response.groups) ? response.groups : []).flatMap((group) => {
      const centres = (Array.isArray(group.centres) ? group.centres : []).map((centre) => ({
        ...centre,
        areaManagerName: group.areaManagerName,
        areaManagerEmail: group.areaManagerEmail,
      }));

      // Area managers have their own scorecards, so surface them as a
      // selectable entry within their group rather than just a header.
      const areaManagerEntry = group.areaManagerEmail
        ? [{
            employeeEmail: group.areaManagerEmail,
            employeeName: group.areaManagerName,
            costCentreName: "Area Manager",
            goalCount: group.totalGoalCount ?? 0,
            areaManagerName: group.areaManagerName,
            areaManagerEmail: group.areaManagerEmail,
            isAreaManager: true,
          }]
        : [];

      return [...areaManagerEntry, ...centres];
    });
  }

  return Array.isArray(response.subordinates) ? response.subordinates : [];
}

export function getGroupStatus(groupBandCounts, groupMissingCount) {
  if (groupBandCounts.failed > 0) return "failed";
  if (groupBandCounts.warning > 0) return "warning";
  if (groupMissingCount > 0) return "missing";
  if (groupBandCounts.passed > 0) return "passed";
  return "neutral";
}
